import { atom } from 'jotai';
import { isEmpty } from 'lodash-es';
import { activeAccountAtom } from 'src/contexts/AppContext/AppContext.atom';
import { EventConfigClient } from 'src/WebApiController';

// Combined state atom
type EventLastReviewedState = {
  data: Record<string, string>;
  isLoading: boolean;
  isCompleted: boolean;
};

export const eventLastReviewedStateAtom = atom<EventLastReviewedState>({
  data: {},
  isLoading: false,
  isCompleted: false,
});

// Write-only atom to handle setting virtual IDs and fetching data
export const setVirtualIdsAtom = atom(
  null,
  async (get, set, virtualViaIds: string[]) => {
    const { webClientConfig } = get(activeAccountAtom);
    const state = get(eventLastReviewedStateAtom);

    if (!webClientConfig) {
      return;
    }

    // Filter out IDs that already have data
    const existingIds = Object.entries(state.data).map(([id, _]) => id);
    const idsToFetch = virtualViaIds.filter((id) => !existingIds.includes(id));

    if (isEmpty(idsToFetch)) {
      return;
    }

    // Fetch the data
    if (webClientConfig.activeAccountId) {
      set(eventLastReviewedStateAtom, (prev) => ({ ...prev, isLoading: true }));
      try {
        const response = await new EventConfigClient(
          webClientConfig
        ).getLastReviewedDateTimeForEvents(idsToFetch);

        if (response && !isEmpty(response)) {
          // Merge new data with existing data
          set(eventLastReviewedStateAtom, (prev) => ({
            ...prev,
            data: { ...prev.data, ...response },
            isLoading: false,
          }));
        } else {
          set(eventLastReviewedStateAtom, (prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      } catch (error) {
        set(eventLastReviewedStateAtom, (prev) => ({
          ...prev,
          isLoading: false,
        }));
        throw new Error('Failed to fetch last reviewed date time for events');
      }
    }
  }
);

// Write-only atom to handle updating last reviewed date time
export const updateLastReviewedDateTimeAtom = atom(
  null,
  async (get, set, viagVirtId: string | null) => {
    const lastReviewedDateTime = new Date().toISOString();
    const { webClientConfig } = get(activeAccountAtom);

    if (!webClientConfig || !viagVirtId) {
      return false;
    }

    // Optimistically update the data and set loading/completed states
    set(eventLastReviewedStateAtom, (prev) => ({
      ...prev,
      isLoading: true,
      isCompleted: false,
    }));

    try {
      await new EventConfigClient(
        webClientConfig
      ).updateLastReviewedDateTimeForEvent(viagVirtId, lastReviewedDateTime);

      set(eventLastReviewedStateAtom, (prev) => ({
        ...prev,
        data: { ...prev.data, [viagVirtId]: lastReviewedDateTime },
        isLoading: false,
        isCompleted: true,
      }));
      return true;
    } catch (error) {
      // Rollback on error
      set(eventLastReviewedStateAtom, (prev) => ({
        ...prev,
        isLoading: false,
        isCompleted: false,
      }));
      throw new Error('Failed to update last reviewed date time for event');
    }
  }
);
